import React from 'react';
import { useTranslation } from 'react-i18next';
import { Award, Users, Target, Heart } from 'lucide-react';

export const About = () => {
  const { t } = useTranslation();

  const values = [
    {
      icon: Target,
      title: 'Our Mission',
      description: 'To deliver exceptional construction and remodeling services that exceed expectations and transform spaces into beautiful, functional environments.',
    },
    {
      icon: Heart,
      title: 'Our Values',
      description: 'We are committed to integrity, quality craftsmanship, customer satisfaction, and building lasting relationships with our clients.',
    },
    {
      icon: Award,
      title: 'Our Excellence',
      description: 'With years of experience and a dedication to perfection, we bring expertise and professionalism to every project we undertake.',
    },
    {
      icon: Users,
      title: 'Our Team',
      description: 'Our skilled professionals are licensed, insured, and passionate about delivering superior results on time and within budget.',
    },
  ];

  return (
    <div data-testid="about-page">
      {/* Hero Section */}
      <section className="py-32 bg-[hsl(215,75%,16%)] text-white text-center">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1
            className="text-5xl sm:text-6xl font-bold mb-6 tracking-tight"
            style={{ fontFamily: 'Outfit' }}
          >
            About EDX Construction
          </h1>
          <p className="text-xl sm:text-2xl leading-relaxed">
            Building Excellence, Creating Lasting Value
          </p>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2
            className="text-4xl font-bold text-[hsl(215,75%,16%)] mb-8 text-center"
            style={{ fontFamily: 'Outfit' }}
          >
            Our Story
          </h2>
          <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
            <p>
              EDX Construction is a premier construction and remodeling company serving Central Florida with a commitment to excellence and customer satisfaction. We specialize in brick pavers, kitchen and bathroom remodeling, interior renovation, and general construction services.
            </p>
            <p>
              Our team of skilled professionals brings years of experience and expertise to every project, ensuring that each job is completed to the highest standards of quality and craftsmanship. We take pride in our attention to detail, transparent communication, and dedication to exceeding our clients' expectations.
            </p>
            <p>
              Whether you're looking to enhance your outdoor living space with beautiful brick pavers, transform your kitchen into a culinary masterpiece, or renovate your entire home, EDX Construction has the skills, experience, and passion to bring your vision to life.
            </p>
          </div>
        </div>
      </section>

      {/* Values Grid */}
      <section className="py-24 bg-[hsl(210,40%,98%)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2
            className="text-4xl font-bold text-[hsl(215,75%,16%)] mb-16 text-center"
            style={{ fontFamily: 'Outfit' }}
          >
            What Sets Us Apart
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div
                  key={index}
                  className="bg-white rounded-lg p-8 card-hover"
                  data-testid={`value-card-${index}`}
                >
                  <div className="flex items-start space-x-6">
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 bg-[hsl(215,75%,16%)] rounded-full flex items-center justify-center">
                        <Icon className="w-8 h-8 text-white" />
                      </div>
                    </div>
                    <div>
                      <h3
                        className="text-2xl font-bold text-[hsl(215,75%,16%)] mb-3"
                        style={{ fontFamily: 'Outfit' }}
                      >
                        {value.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {value.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Credentials */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2
            className="text-4xl font-bold text-[hsl(215,75%,16%)] mb-12"
            style={{ fontFamily: 'Outfit' }}
          >
            Licensed, Insured & Certified
          </h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-8">
            EDX Construction is fully licensed and insured to provide construction and remodeling services throughout Central Florida. Our team adheres to all local building codes and regulations, and we maintain comprehensive insurance coverage to protect our clients and our work.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
            <div className="bg-[hsl(210,40%,98%)] rounded-lg p-6">
              <div className="text-3xl font-bold text-[hsl(215,75%,16%)] mb-2">10+</div>
              <div className="text-gray-600">Years Experience</div>
            </div>
            <div className="bg-[hsl(210,40%,98%)] rounded-lg p-6">
              <div className="text-3xl font-bold text-[hsl(215,75%,16%)] mb-2">500+</div>
              <div className="text-gray-600">Projects Completed</div>
            </div>
            <div className="bg-[hsl(210,40%,98%)] rounded-lg p-6">
              <div className="text-3xl font-bold text-[hsl(215,75%,16%)] mb-2">100%</div>
              <div className="text-gray-600">Client Satisfaction</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};